import { DateTime } from "luxon";

// Grid geometry is in rem so an hour row grows with the user's font size and the
// booking labels inside it keep fitting. Kept as bare numbers because positions
// are derived arithmetically (i * HOUR_REM); rem() applies the unit at the edge.
export const HOUR_REM = 3.5;
export const TIME_GUTTER_REM = 3.25;
export const COLUMN_MIN_WIDTH_REM = 7.5;
export const MIN_BLOCK_REM = 1.625;

export const rem = (n) => `${n}rem`;
const DEFAULT_START_MIN = 8 * 60;
const DEFAULT_END_MIN = 20 * 60;
const MIN_SPAN_MIN = 4 * 60;
const DAY_MAX_MIN = 1440;

// Bookish stores time/endTime as minutes-since-midnight (e.g. 1200 = 8:00 PM).
// Values outside a day are rejected: some records hold an ISO string in endTime,
// which parses to a nonsense minute count and would otherwise stretch a block
// across the whole grid and collide with every later booking.
export function toMinutes(raw) {
  const m = parseInt(raw, 10);
  return Number.isNaN(m) || m < 0 || m > DAY_MAX_MIN ? null : m;
}

export function weekDatesFrom(weekStart) {
  if (!weekStart) return [];
  const start = DateTime.fromISO(weekStart, { zone: "UTC" });
  return Array.from({ length: 7 }, (_, i) => start.plus({ days: i }).toFormat("yyyy-MM-dd"));
}

// Day-header columns for a week. The month label says which month the day
// numbers belong to — a week crossing into the next one otherwise reads as a
// jump backwards.
// Minutes past midnight right now on the club's wall clock. Falls back to the
// device zone when the club zone is unknown.
export function clubNowMinutes(timeZone) {
  const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
  return now.hour * 60 + now.minute;
}

export function weekDayColumns(dates, todayYmd) {
  return dates.map((d) => {
    const dt = DateTime.fromISO(d, { zone: "UTC" });
    return { key: d, topLabel: dt.toFormat("ccc"), mainLabel: dt.toFormat("d"), subLabel: dt.toFormat("LLL"), highlight: d === todayYmd };
  });
}

// Greedy lane packing so overlapping bookings sit side by side instead of
// stacking on top of each other. Returns each item's lane plus the day's lane count.
export function packLanes(items) {
  const sorted = [...items].sort((a, b) => a.start - b.start || a.end - b.end);
  const laneEnds = [];
  const placed = sorted.map((it) => {
    let lane = laneEnds.findIndex((end) => end <= it.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.end);
    } else {
      laneEnds[lane] = it.end;
    }
    return { ...it, lane };
  });
  return { placed, laneCount: Math.max(1, laneEnds.length) };
}

// A booking as a positioned item: start/end in minutes-of-day. Missing or
// out-of-range start → null (caller drops it); missing/invalid end defaults to
// a one-hour block, clamped to the day edge.
export function toPositionedItem(booking) {
  const start = toMinutes(booking.time);
  if (start === null) return null;
  let end = toMinutes(booking.endTime);
  if (end === null || end <= start) end = start + 60;
  if (end > DAY_MAX_MIN) end = DAY_MAX_MIN;
  return { booking, start, end };
}

// A day's bookings as positioned items keyed by date; bookings missing a start
// time are dropped.
export function buildDayItems(days) {
  const byDate = new Map();
  for (const day of days) {
    byDate.set(day.date, day.bookings.map(toPositionedItem).filter(Boolean));
  }
  return byDate;
}

// Fit the visible hour window to the bookings. Without a base range the window
// hugs the data (default 8:00–20:00 when empty, min 4h span) — the original
// TrainerWeekGrid behavior. With { startHour, endHour } (the saas schedule
// hours) the window is at least that range, expanded if bookings overflow it.
export function computeGridBounds(allItems, { startHour, endHour } = {}) {
  const hasBase = Number.isFinite(startHour) && Number.isFinite(endHour);
  let gridStartMin = hasBase ? startHour * 60 : DEFAULT_START_MIN;
  let gridEndMin = hasBase ? endHour * 60 : DEFAULT_END_MIN;
  if (allItems.length) {
    const minStart = Math.max(0, Math.floor(Math.min(...allItems.map((it) => it.start)) / 60) * 60);
    const maxEnd = Math.min(DAY_MAX_MIN, Math.ceil(Math.max(...allItems.map((it) => it.end)) / 60) * 60);
    gridStartMin = hasBase ? Math.min(gridStartMin, minStart) : minStart;
    gridEndMin = hasBase ? Math.max(gridEndMin, maxEnd) : maxEnd;
  }
  if (gridEndMin - gridStartMin < MIN_SPAN_MIN) gridEndMin = Math.min(DAY_MAX_MIN, gridStartMin + MIN_SPAN_MIN);

  const hourCount = Math.max(1, (gridEndMin - gridStartMin) / 60);
  const bodyHeightRem = hourCount * HOUR_REM;
  const hours = Array.from({ length: hourCount + 1 }, (_, i) => gridStartMin + i * 60);
  return { gridStartMin, hourCount, bodyHeightRem, hours };
}
